import React, { useEffect, useRef, useState } from "react";

import {
  RideButton,
  RideGlDown,
  RideGlPlus,
  RideBadge,
} from "@rimac-seguros/ride-system-components";
import { useSelector } from "react-redux";
import Lottie from "lottie-react";

import { formatAmountThousand } from "@/utils/Utils";
import loadingAnimated from "@/assets/animation/loading-animated.json";
import { RATES_RESTRICTED_PERCENTAGES } from "../../constants/step-restrictions";

import confetti from "@/assets/animation/confetti.json";
import "./styles.scss";
import { formatAndMaskFullName } from '@/utils/format-name'

export const CardDetailPlan = ({
  isLoadingData = false,
  callAnalyticsAction,
  shouldDisplayBeneficiaries = false
}) => {
  const [showMore, setShowMore] = useState(false);

  const { nameFrequency, typeOfFinancing, planSelected } = useSelector(
    (state) => state.plans
  );
  const { beneficiaryType, beneficiaries } = useSelector((state) => state.beneficiaries);
  const isLegalBeneficiary = beneficiaryType === "beneficiary-legal";

  const lottieRef = useRef();
  const lottieConfettiRef = useRef();
  const lottieConfetti2Ref = useRef();
  
  const { devolucionTotal, aporte, ganancia } = typeOfFinancing || {};
  const { sumAssured, savingTime, returnPercentage } = planSelected || {};
  const descMoney = sumAssured?.descripcion;
  
  const validateRateAllowed =
    returnPercentage &&
    !RATES_RESTRICTED_PERCENTAGES.includes(Number(returnPercentage.value));

  useEffect(() => {
    if (lottieRef) {
      lottieRef?.current?.setSpeed(1.5);
    }
  }, [lottieRef]);

  useEffect(() => {
    if (lottieConfettiRef) {
      lottieConfettiRef?.current?.setSpeed(1.5);
    }
  }, [lottieConfettiRef]);

  useEffect(() => {
    if (lottieConfetti2Ref) {
      lottieConfetti2Ref?.current?.setSpeed(1.5);
    }
  }, [lottieConfetti2Ref]);

  const handleClickAction = () => {
    if (!showMore) {
      callAnalyticsAction();
    }
    setShowMore(!showMore);
  };

  return (
    <div className="card-detail-plan-ve w-full border rounded-ride-sys-border-radius-container-large border-ride-sys-color-border-default lg:sticky lg:top-24">
      <div className="p-ride-gl-spacing-large">
        <div
          className={`card-detail-plan-ve_container relative overflow-hidden ${
            isLoadingData ? "blink-loading" : ""
          }`}
        >
          <div className="card-detail-plan-ve_confetti">
            <Lottie
              lottieRef={lottieConfettiRef}
              animationData={confetti}
              autoplay
            />
            <Lottie
              lottieRef={lottieConfetti2Ref}
              animationData={confetti}
              autoplay
            />
          </div>
          <div className="flex items-center justify-between p-ride-gl-spacing-xlarge">
            <div className="relative flex flex-col w-full gap-ride-gl-spacing-large">
              <div>
                <div className="card-detail-plan-ve_value-amount relative mb-1">
                  {isLoadingData ? (
                    <>
                      <Lottie
                        lottieRef={lottieRef}
                        animationData={loadingAnimated}
                        autoplay
                        className="card-detail-plan-ve_animated-amount animated-main"
                      />
                    </>
                  ) : (
                    <>{`${descMoney}${formatAmountThousand(
                      Number(devolucionTotal || 0).toFixed(2)
                    )}`}</>
                  )}
                </div>
                Es lo que recibirás al elegir el{" "}
                <span className="ride-sys-text-label-xlarge-default">
                  Seguro de Vida Endosable RIMAC
                </span>
              </div>
            </div>
          </div>
        </div>
        <div
          className={`flex flex-col gap-ride-gl-spacing-large ${
            showMore ? "expand" : "collapse"
          }`}
        >
          <div className="px-ride-gl-spacing-2xlarge">
            {validateRateAllowed && (
              <div className="flex items-center justify-between ride-sys-text-paragraph-large-bold py-ride-gl-spacing-xlarge px-ride-gl-spacing-5xlarge">
                <div className="flex flex-col text-center">
                  <span>Aporte total</span>
                  <span className="relative">
                    {isLoadingData ? (
                      <>
                        &nbsp;
                        <Lottie
                          lottieRef={lottieRef}
                          animationData={loadingAnimated}
                          autoplay
                          className="card-detail-plan-ve_animated-amount"
                          style={{ right: "-35px" }}
                        />
                      </>
                    ) : (
                      <>{`${descMoney}${formatAmountThousand(
                        Number(aporte || 0).toFixed(2)
                      )}`}</>
                    )}
                  </span>
                </div>
                <div className="flex flex-col text-center">
                  <RideGlPlus size="medium" />
                </div>
                <div className="flex flex-col text-center">
                  <span>Ganancias</span>
                  <span className="relative">
                    {isLoadingData ? (
                      <>
                        &nbsp;
                        <Lottie
                          lottieRef={lottieRef}
                          animationData={loadingAnimated}
                          autoplay
                          className="card-detail-plan-ve_animated-amount"
                          style={{ right: "-32px" }}
                        />
                      </>
                    ) : (
                      <>{`${descMoney}${formatAmountThousand(
                        Number(ganancia || 0).toFixed(2)
                      )}`}</>
                    )}
                  </span>
                </div>
              </div>
            )}
            <div className={`flex flex-col gap-ride-gl-spacing-large py-ride-gl-spacing-xlarge ${validateRateAllowed && 'border-t-ride-gl-border-width-thin border-ride-sys-color-border-default'}`}>
              <div className="flex justify-between ride-sys-text-paragraph-medium-regular-short">
                <span>Tiempo de ahorro</span>
                <span className="relative">
                  <>{isLoadingData ? <Lottie lottieRef={lottieRef} animationData={loadingAnimated} autoplay className="card-detail-plan_animated-amount" style={{ right: "-35px" }} /> : `${savingTime?.text} años`}</>
                </span>
              </div>
              <div className="flex justify-between ride-sys-text-paragraph-medium-regular-short">
                <span>Porcentaje de devolución</span>
                <span className="relative">
                  {isLoadingData ? (
                    <Lottie
                      lottieRef={lottieRef}
                      animationData={loadingAnimated}
                      autoplay
                      className="card-detail-plan_animated-amount"
                      style={{ right: "-35px" }}
                    />
                  ) : (
                    <RideBadge
                      case="promotional"
                      primaryText={returnPercentage?.text}
                    />
                  )}
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-ride-gl-spacing-large border-t-ride-gl-border-width-thin border-ride-sys-color-border-default py-ride-gl-spacing-xlarge">
              <div className="flex justify-between ride-sys-text-paragraph-medium-regular-short">
                <span>Cobertura por fallecimiento</span>
                <span className="relative">{isLoadingData ? <Lottie lottieRef={lottieRef} animationData={loadingAnimated} autoplay className="card-detail-plan_animated-amount" style={{ right: "-35px" }} /> : sumAssured?.text}</span>
              </div>
              <div className="flex justify-between ride-sys-text-paragraph-medium-regular-short">
                <span>Frecuencia de pago</span>
                <span className="relative capitalize">{isLoadingData ? <Lottie lottieRef={lottieRef} animationData={loadingAnimated} autoplay className="card-detail-plan_animated-amount" style={{ right: "-35px" }} /> : nameFrequency}</span>
              </div>
            </div>
            {
              shouldDisplayBeneficiaries && beneficiaryType && (
                <div className="flex flex-col gap-ride-gl-spacing-large border-t-ride-gl-border-width-thin border-ride-sys-color-border-default py-ride-gl-spacing-xlarge">
                  {
                    isLegalBeneficiary ? (
                      <>
                        <p className="ride-sys-text-paragraph-small-bold">Beneficiarios</p>
                        <p className='ride-sys-text-paragraph-medium-regular-short'>Herederos legales</p>
                      </>
                    ) : (
                      <>
                        <p className="ride-sys-text-paragraph-small-bold">Beneficiarios ({beneficiaries.length})</p>
                        {
                          beneficiaries.map(beneficiary => (
                            <div key={beneficiary?.documentNumber} className="flex justify-between ride-sys-text-paragraph-medium-regular-short">
                              <span>{formatAndMaskFullName(beneficiary.names, beneficiary.lastName, beneficiary.surName)}</span>
                              <span>{beneficiary.percentage}%</span>
                            </div>
                          ))
                        }
                      </>
                    )
                  }
                </div>
              )
            }
          </div>
        </div>
      </div>
      <div className="border-t border-ride-sys-color-border-default">
        <RideButton
          text={!showMore ? "Ver detalle" : "Ocultar detalle"}
          variant="text-primary"
          size="large"
          glyph={
            <RideGlDown
              className={
                showMore ? "transform rotate-180 transition-all delay-200" : ""
              }
            />
          }
          glyphPosition="right"
          className="w-full"
          disabled={isLoadingData}
          onClick={handleClickAction}
        />
      </div>
    </div>
  );
};
